/**
 * TinyMCE RTL Auto-Detect Plugin
 *
 * Registers a TinyMCE plugin that automatically applies per-paragraph
 * `dir` attributes (`rtl` or `ltr`) based on the text content of each
 * block element. This makes Arabic, Hebrew, and other RTL scripts display
 * correctly alongside left-to-right content in the same editor.
 *
 * Usage:
 *   import { registerRtlAutoDetectPlugin } from './rtl-auto-detect.plugin';
 *
 *   // Call once before tinymce.init() – typically in the module/component constructor
 *   registerRtlAutoDetectPlugin();
 *
 *   // Add 'rtl-auto-detect' to the plugins array in tinymce.init()
 *   plugins: ['table', 'lists', 'link', 'code', 'mentions', 'rtl-auto-detect'],
 *
 * Shared between Relay Messenger and Message Builder.
 */

import tinymce from 'tinymce';
import { detectTextDirection } from '../utils/rtl-detect.util';

/** CSS block-level selectors whose direction we auto-detect */
const BLOCK_SELECTOR = 'p, div, li, h1, h2, h3, h4, blockquote, pre';

/**
 * Walks every block element in the editor body and sets its `dir` attribute
 * to `'rtl'` or `'ltr'` based on whether the element's text contains RTL chars.
 * Blocks with no visible text are left unchanged.
 *
 * For mixed-content blocks (Arabic + English in sibling spans), each child span
 * receives `dir="auto"` so the browser resolves each span's direction
 * independently from its own text, overriding the inherited block direction.
 * This prevents English spans from being pulled right-to-left when they sit
 * inside an otherwise-RTL block.
 */
function applyBlockDirections(editor: any): void {
  const body: HTMLElement = editor.getBody();
  if (!body) return;

  body.querySelectorAll<HTMLElement>(BLOCK_SELECTOR).forEach((block) => {
    const text = block.textContent ?? '';
    if (text.trim().length === 0) return;

    block.setAttribute('dir', detectTextDirection(text));

    // Apply dir="auto" to every non-empty child span so that each span
    // independently determines its direction from its own first strong
    // directional character, rather than inheriting the block's direction.
    // This is the key fix for mixed Arabic/English content in the same block.
    block.querySelectorAll<HTMLElement>('span').forEach((span) => {
      if ((span.textContent ?? '').trim().length > 0) {
        span.setAttribute('dir', 'auto');
      }
    });
  });
}

let registered = false;

/**
 * Registers the `rtl-auto-detect` TinyMCE plugin.
 * Safe to call multiple times – registers only once.
 */
export function registerRtlAutoDetectPlugin(): void {
  if (registered) return;
  registered = true;

  tinymce.PluginManager.add('rtl-auto-detect', (editor) => {
    // Re-apply directions whenever content changes or is programmatically set
    editor.on('input change keyup SetContent', () => {
      applyBlockDirections(editor);
    });

    // Paste arrives after PastePostProcess so we defer slightly to let DOM settle
    editor.on('PastePostProcess', () => {
      applyBlockDirections(editor);
    });

    // Initial pass once the editor content is fully loaded
    editor.on('init', () => {
      applyBlockDirections(editor);
    });
  });
}
